import { useEffect, useState } from 'react';

/**
 * Ein Router in fünfzig Zeilen, auf echten Pfaden.
 *
 * Acht Seiten brauchen keine Bibliothek. Gelesen wird `location.pathname` –
 * kein `#`: Die Adresse eines Rapports soll sich verschicken und
 * vorlesen lassen wie jede andere. Dass ein Tiefenlink auch beim ersten
 * Aufruf ankommt, liefert die API: Alles außerhalb von `/api`, das keine
 * Datei trifft, bekommt `index.html` (`apps/api/src/http/app.ts`).
 */
export type Route =
  | { seite: 'liste' }
  | { seite: 'neu' }
  | { seite: 'rapport'; id: string }
  | { seite: 'bearbeiten'; id: string }
  | { seite: 'unterschrift'; id: string }
  | { seite: 'einsaetze' }
  | { seite: 'auslastung' }
  | { seite: 'taetigkeitsarten' }
  | { seite: 'freigabe' }
  | { seite: 'versand' }
  | { seite: 'einstellungen' };

export function routeAus(pfad: string): Route {
  /*
   * Das `#` wird weiterhin abgeschnitten – alte Lesezeichen und die
   * Adressen, die bisher verschickt wurden, tragen es noch, und
   * `/#/rapport/…` soll denselben Rapport zeigen wie `/rapport/…`.
   */
  const teile = pfad
    .replace(/^#/, '')
    .replace(/^\/#\//, '/')
    .split('/')
    .filter(Boolean);

  if (teile[0] === 'neu') return { seite: 'neu' };
  if (teile[0] === 'einsaetze') return { seite: 'einsaetze' };
  if (teile[0] === 'auslastung') return { seite: 'auslastung' };
  if (teile[0] === 'taetigkeitsarten') return { seite: 'taetigkeitsarten' };
  if (teile[0] === 'freigabe') return { seite: 'freigabe' };
  if (teile[0] === 'versand') return { seite: 'versand' };
  if (teile[0] === 'einstellungen') return { seite: 'einstellungen' };

  if (teile[0] === 'rapport' && teile[1]) {
    if (teile[2] === 'bearbeiten') return { seite: 'bearbeiten', id: teile[1] };
    if (teile[2] === 'unterschrift') return { seite: 'unterschrift', id: teile[1] };

    return { seite: 'rapport', id: teile[1] };
  }

  return { seite: 'liste' };
}

/**
 * `pushState` löst kein Ereignis aus – `popstate` kommt nur vom Zurück-Knopf.
 * Also eines von Hand, an dem `useRoute` hängt.
 */
const EREIGNIS = 'arbeitsrapporte:navigation';

function aktuellerPfad(): string {
  return window.location.pathname + window.location.hash;
}

/**
 * Der Wächter vor dem Verlassen einer Seite.
 *
 * Das Formular meldet sich hier an, solange etwas Ungespeichertes darin
 * steht – und wird gefragt, bevor ein Verweis oder `navigiere` die Seite
 * wechselt. Es gibt höchstens einen: Es ist immer nur eine Seite offen.
 * Den Zurück-Knopf des Browsers fängt das nicht, dafür steht
 * `beforeunload` im Formular selbst.
 */
let waechter: (() => boolean) | null = null;

export function setzeWaechter(pruefen: (() => boolean) | null): void {
  waechter = pruefen;
}

function darfVerlassen(): boolean {
  return waechter === null || waechter();
}

export function navigiere(pfad: string, optionen: { ohneWaechter?: boolean } = {}): void {
  if (pfad === window.location.pathname) return;
  if (!optionen.ohneWaechter && !darfVerlassen()) return;

  waechter = null;
  window.history.pushState(null, '', pfad);
  window.dispatchEvent(new Event(EREIGNIS));
}

/**
 * Ein Verweis bleibt ein `<a href="/…">` – mit Adresse in der Statusleiste,
 * „in neuem Tab öffnen" und Mittelklick. Nur der einfache Klick wird
 * abgefangen, damit die Seite nicht neu lädt.
 */
function fangeVerweis(ereignis: MouseEvent): void {
  if (ereignis.defaultPrevented || ereignis.button !== 0) return;
  if (ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return;

  const ziel = ereignis.target;
  const verweis = ziel instanceof Element ? ziel.closest('a') : null;

  if (!verweis || verweis.hasAttribute('download')) return;
  if (verweis.target && verweis.target !== '_self') return;
  if (verweis.getAttribute('rel') === 'external') return;

  const adresse = new URL(verweis.href, window.location.href);

  if (adresse.origin !== window.location.origin) return;

  ereignis.preventDefault();

  if (adresse.pathname === window.location.pathname) return;
  if (!darfVerlassen()) return;

  waechter = null;
  window.history.pushState(null, '', adresse.pathname + adresse.search);
  window.dispatchEvent(new Event(EREIGNIS));
}

window.addEventListener('click', fangeVerweis);

/**
 * Alte Adressen mit `#` einmal geradeziehen.
 *
 * Wer `/#/rapport/…` im Lesezeichen hat oder verschickt bekommen hat, soll
 * denselben Rapport sehen – und danach die saubere Adresse in der Leiste.
 * `replaceState`, nicht `pushState`: Der Zurück-Knopf soll nicht auf die
 * alte Fassung derselben Seite zeigen.
 *
 * Die Antwort von Entra steht ebenfalls im `#` (`#code=…`), beginnt aber nie
 * mit `#/` – sie bleibt liegen, und MSAL räumt sie selbst weg.
 */
export function raeumeHashAdresseAuf(): void {
  if (!window.location.hash.startsWith('#/')) return;

  window.history.replaceState(null, '', window.location.hash.slice(1));
  window.dispatchEvent(new Event(EREIGNIS));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => routeAus(aktuellerPfad()));

  useEffect(() => {
    const aktualisiere = () => setRoute(routeAus(aktuellerPfad()));

    /*
     * Beim Einhängen einmal lesen: Zwischen dem ersten `useState` und dem
     * Effekt kann schon navigiert worden sein – MSAL räumt nach der
     * Anmeldung die Adresse auf.
     */
    aktualisiere();

    window.addEventListener('popstate', aktualisiere);
    window.addEventListener(EREIGNIS, aktualisiere);

    return () => {
      window.removeEventListener('popstate', aktualisiere);
      window.removeEventListener(EREIGNIS, aktualisiere);
    };
  }, []);

  return route;
}
